import { useNavigate } from 'react-router-dom';
import {
  Button,
  ButtonType,
  ButtonSize,
  IconName,
  TagVariant,
} from '@hills-design-system/components';
import { Card } from '../../../components/Card/Card';
import {
  usePetParentReportState,
  usePetParentReportActions,
} from '../../../hooks/usePetParentReports';
import {
  useAdoptionReportState,
  useAdoptionReportActions,
} from '../../../hooks/useAdoptionReports';
import { format, addDays, addMonths } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import {
  getHighestPriorityPendingReport,
  getDueDateTagVariant,
  calculatePetParentReportingWindow,
  getPetParentReportMondayDate,
  utcCalendarDateToLocal,
  formatPetParentWindowFromTimeframe,
  formatPetParentMondayFromTimeframe,
} from '../../../utils/dateFormatHelpers';
import { useEffect } from 'react';
import { Report, ReportStatus } from '../../../types/reports';
import { PetParentReport } from '../../../types/petParentReports';
import { AdoptionReport } from '../../../types/adoptionReports';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';
import Banner from '../../../components/Banner';
import { Tooltip } from '../../../components/Tooltip';
import { useShelterState } from '../../../hooks/useShelter';
import { trackEvent } from '../../../services/analytics';

// --- Helper Functions ---
const generateReportTitle = (
  report: Report,
  t: (key: TranslationKeyEnum, ...args: (string | number | Date)[]) => string
): string => {
  // Use dueDate as a fallback if timeframeStart is not available
  const startDate = utcCalendarDateToLocal(report.timeframeStart ?? report.dueDate) ?? new Date();

  if (report.type === 'petParent') {
    // subtracting 3 days from the dueDate as the timeframe for report submission in 12 AM Monday to 11:59 PM Sunday
    const endDate = toZonedTime(addDays(report.dueDate, -3), 'UTC');

    // Format date range: if same month, show "Jan. 12–18", otherwise "Jan. 31–Feb. 6"
    const dateRange =
      format(startDate, 'MMM') === format(endDate, 'MMM')
        ? `${format(startDate, 'MMM. d–')}${format(endDate, 'd')}`
        : `${format(startDate, 'MMM. d–')}${format(endDate, 'MMM. d')}`;
    return t(TranslationKeyEnum.SubmitPetParentReport, dateRange);
  } else {
    // Monthly adoption report
    return t(TranslationKeyEnum.SubmitMonthlyAdoptions, format(startDate, 'MMMM'));
  }
};

const formatDueDateText = (
  report: Report,
  t: (key: TranslationKeyEnum, ...args: (string | number | Date)[]) => string
): string => {
  const date = toZonedTime(report.dueDate, 'UTC');
  const formattedDate = format(date, 'EEEE, MMM. d');

  if (report.status === 'missed') {
    return t(TranslationKeyEnum.WasDue).replace('{0}', formattedDate);
  }
  // For pending reports, pass the Date object directly
  return t(TranslationKeyEnum.Due).replace('{0}', formattedDate);
};

// --- Components ---

// Report Card for Pending reports
const ReportCard = ({ report }: { report: Report }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const title = generateReportTitle(report, t);
  const dueDateText = formatDueDateText(report, t);
  const tagVariant = getDueDateTagVariant(report.status, report.isWarning);

  // Determine styling based on due date proximity
  const isUrgent = tagVariant === TagVariant.Yellow;
  const statusStyles = isUrgent
    ? 'bg-yellow-tag-bg border-yellow-tag-border'
    : 'bg-blue-tag-bg border-blue-tag-border';
  const textStyles = isUrgent ? 'text-yellow-tag-text' : 'text-blue-tag-text';

  return (
    <Card
      padding="large"
      radius="large"
      customBackground="bg-gray-50"
      height="h-[280px]"
      shadow={true}
      className="flex flex-col lg:flex-row justify-between lg:items-end overflow-visible"
    >
      <div className="flex flex-col gap-2 lg:justify-between h-full">
        <h3 className="text-heading-200 font-bold text-black/90">{title}</h3>
        <div
          className={`inline-flex items-center gap-2 px-3.5 py-1 h-fit border w-max rounded-full ${statusStyles}`}
        >
          <Tooltip
            align={report.type === 'adoption' ? 'top' : 'center'}
            size={16}
            textClasses={textStyles}
            content={
              report.type === 'adoption' ? (
                <div className="w-[360px]  p-4 flex flex-col gap-4">
                  <div>
                    {t(TranslationKeyEnum.AdoptionTooltipPrefix)}{' '}
                    {t(TranslationKeyEnum.AdoptionTooltipLinkText)}
                    {/* temporarily commenting until we have the document link */}
                    {/* <a href="#" className="underline">
                                        {t(TranslationKeyEnum.AdoptionTooltipLinkText)}
                                      </a> */}
                  </div>
                  <div>{t(TranslationKeyEnum.AdoptionTooltipSuffix)}</div>
                </div>
              ) : (
                <div className="w-[360px]  p-4">
                  <div className="space-y-2">
                    <div className="text-sm font-light text-white leading-[1.375]">
                      {t(TranslationKeyEnum.PetParentReportsSubmitTooltipTitleHomePage)}
                    </div>
                    <div className="text-sm font-light text-white leading-[1.375]">
                      {t(TranslationKeyEnum.PetParentReportsSubmitTooltipDescription)}
                    </div>
                  </div>
                </div>
              )
            }
          />

          <span className={`text-sm font-bold ${textStyles}`}>{dueDateText}</span>
        </div>
      </div>
      <Button
        label={t(TranslationKeyEnum.Start)}
        type={ButtonType.Primary}
        size={ButtonSize.Small}
        onClick={() => {
          // Navigate to the reports page with the appropriate tab and auto-open the specific report
          if (report.type === 'petParent') {
            navigate(`/reports?tab=pet-parents&reportId=${report.id}`);
          } else {
            navigate(`/reports?tab=adoptions&reportId=${report.id}`);
          }
        }}
        className="!w-auto shrink-0 whitespace-nowrap !px-[16px] h-[40px]"
      />
    </Card>
  );
};

const WelcomePetParentReportCard = () => {
  const { t } = useTranslation();
  const { shelter } = useShelterState();
  const petParentReports = usePetParentReportState();

  // Prefer backend-calculated timeframe start (uses effective join date incl. debug override).
  // Fall back to client-side calculation from shelter join date.
  const timeframeStart = petParentReports?.firstExpectedReport?.timeframeStart;
  const reportingWindow = timeframeStart
    ? formatPetParentWindowFromTimeframe(timeframeStart)
    : calculatePetParentReportingWindow(shelter?.join_program_date);
  const mondayDate = timeframeStart
    ? formatPetParentMondayFromTimeframe(timeframeStart)
    : getPetParentReportMondayDate(shelter?.join_program_date);

  return (
    <Card
      padding="large"
      radius="large"
      customBackground="bg-gray-50"
      height="h-[280px]"
      shadow={true}
      className="flex justify-between items-end overflow-visible"
    >
      <div className="flex flex-col justify-between h-full">
        <h3 className="text-heading-200 font-bold w-[400px] leading-[1.125em] text-black/90">
          {t(TranslationKeyEnum.NewShelterUserWelcomeTitle, reportingWindow, mondayDate)}
        </h3>
        <div
          className={`inline-flex items-center gap-2 px-3.5 py-1 h-fit border w-max rounded-full bg-blue-tag-bg border-blue-tag-border`}
        >
          <Tooltip
            size={16}
            textClasses={'text-blue-tag-text'}
            content={
              <div className="w-[360px]  p-4">
                <div className="space-y-2">
                  <div className="text-sm font-light text-white leading-[1.375]">
                    {t(TranslationKeyEnum.PetParentReportsSubmitTooltipTitleHomePage)}
                  </div>
                  <div className="text-sm font-light text-white leading-[1.375]">
                    {t(TranslationKeyEnum.PetParentReportsSubmitTooltipDescription)}
                  </div>
                </div>
              </div>
            }
          />{' '}
          <span className={`text-sm font-bold text-blue-tag-text`}>
            {t(TranslationKeyEnum.LearnMore)}
          </span>
        </div>
      </div>
    </Card>
  );
};

const ReportSubmittedCard = ({ report }: { report: PetParentReport }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <Card
      padding="large"
      radius="large"
      customBackground="bg-gray-50"
      height="h-[280px]"
      shadow={true}
      className="flex flex-col lg:flex-row justify-between lg:items-end"
    >
      <div className="flex flex-col lg:justify-between gap-2 h-full">
        <h3 className="text-heading-200 font-bold lg:w-[300px] leading-[1.125em] text-black/90">
          {t(TranslationKeyEnum.PetParentReportSubmittedTitle)}
        </h3>
        <span className={`text-sm font-light`}>
          {t(TranslationKeyEnum.PetParentReportSubmittedCanAddMoreDescription, report.timeframe)}
        </span>
      </div>
      <Button
        label={t(TranslationKeyEnum.AddMore)}
        type={ButtonType.Primary}
        size={ButtonSize.Small}
        onClick={() => {
          // Navigate to the reports page with the appropriate tab and auto-open the specific report
          navigate(`/reports?tab=pet-parents&reportId=${report.id}`);
        }}
        className="!w-fit h-[40px]"
      />
    </Card>
  );
};

// Shared Section Layout Component
export const ReportSectionLayout = ({
  title,
  description,
  children,
  viewPastReportsUrl,
  viewPastReportsLabel,
  showTooltip = false,
  tooltipContent,
  tooltipAlign,
  statusRoundel,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  viewPastReportsUrl: string;
  viewPastReportsLabel?: string;
  showTooltip?: boolean;
  tooltipContent?: React.ReactNode;
  tooltipAlign?: 'left' | 'center' | 'right' | 'top';
  statusRoundel?: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { downloadOptinGuide } = usePetParentReportActions();

  const TooltipContent = () => (
    <div className="w-[360px]  p-4">
      <div className="space-y-2">
        <div className="text-sm font-bold text-white leading-[1.375]">
          {t(TranslationKeyEnum.PetParentReportsTooltipTitle)}
        </div>
        <div className="text-sm font-normal text-white leading-[1.375]">
          {t(TranslationKeyEnum.PetParentReportsTooltipDescription1)}{' '}
          <button
            className="underline bg-transparent p-0 m-0"
            onClick={() => {
              trackEvent({
                action: 'opt-in_guide',
                category: 'pet_parent_report',
                label: t(TranslationKeyEnum.HillsOptInGuide) ?? '',
              });
              downloadOptinGuide();
            }}
          >
            {t(TranslationKeyEnum.HillsOptInGuide)}
          </button>{' '}
          {t(TranslationKeyEnum.PetParentReportsTooltipDescription2)}
        </div>
      </div>
    </div>
  );

  return (
    <section className="flex flex-col lg:flex-row justify-between gap-4 lg:gap-[134px] w-full items-start">
      {/* Left Column with fixed width */}
      <div className="flex flex-col gap-4 lg:w-[301px]">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-heading-125 leading-heading text-black-950">{title}</h2>
            {showTooltip && (
              <Tooltip content={tooltipContent ?? <TooltipContent />} align={tooltipAlign} />
            )}
          </div>
          <p className="font-bold text-heading-125 leading-heading text-black-600">{description}</p>
        </div>
        {statusRoundel && <div>{statusRoundel}</div>}
        <Button
          label={viewPastReportsLabel ?? t(TranslationKeyEnum.ViewPastReports)}
          type={ButtonType.Tertiary}
          size={ButtonSize.Small}
          icon={IconName.ArrowRight}
          align="left"
          className={'hover:bg-transparent w-fit whitespace-nowrap'}
          onClick={() => navigate(viewPastReportsUrl)}
        />
      </div>
      {/* Right Column taking remaining space */}
      <div className="w-full flex flex-col gap-2">
        {/* Allow vertical stacking for banner + card */}
        {children}
      </div>
    </section>
  );
};

// Pet Parent Reports Section
export const PetParentReportsSection = () => {
  const petParentReports = usePetParentReportState();
  const petParentActions = usePetParentReportActions();

  const { getPetParentReports } = petParentActions ?? {};
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (getPetParentReports && typeof getPetParentReports === 'function') {
      getPetParentReports('all', 1, 5);
    }
  }, [getPetParentReports]);

  // Get highest priority pending pet parent report
  const pendingPetParentReport = petParentReports?.data
    ? getHighestPriorityPendingReport(petParentReports.data)
    : undefined;

  // Get submitted report for the current timeframe
  const submittedReport =
    petParentReports?.data?.filter(
      (report) => report.status === 'submitted' && report.canAddMore === true
    )[0] ?? null;

  // Get missed reports that can still be submitted (actionable missed reports)
  const missedPetParentReports =
    petParentReports?.data?.filter(
      (report) => report.status === 'missed' && report.canSubmit === true
    ) ?? [];

  const handleBannerAction = (targetTab: 'pet-parents' | 'adoptions') => {
    // Navigate to reports page with the specific report ID to auto-open modal
    navigate(`/reports?tab=${targetTab}`);
  };

  // Convert to Report format for ReportCard and MissedReportBanner
  const convertPetParentToReport = (petParentReport: PetParentReport): Report => {
    let status: ReportStatus;
    if (petParentReport.status === 'due') {
      status = ReportStatus.Pending;
    } else if (petParentReport.status === 'missed') {
      status = ReportStatus.Missed;
    } else if (petParentReport.status === 'late') {
      status = ReportStatus.Late;
    } else {
      status = ReportStatus.Submitted;
    }
    return {
      id: petParentReport.id,
      type: 'petParent',
      status,
      dueDate: petParentReport.dueDate ?? '',
      timeframeStart: petParentReport.timeframe_start,
      isWarning: petParentReport.isWarning,
    };
  };

  return (
    <ReportSectionLayout
      title={t(TranslationKeyEnum.PetParentReports)}
      description={t(TranslationKeyEnum.SubmitOptedInPetParents)}
      viewPastReportsUrl="/reports?tab=pet-parents"
      showTooltip={true}
    >
      {petParentReports === null ? (
        <div>{t(TranslationKeyEnum.LoadingPetParentReports)}</div>
      ) : (
        <>
          {/* if no data, then show welcome card */}
          {petParentReports.data.length === 0 && <WelcomePetParentReportCard />}

          {/* If no pending report, and is submitted then show this card */}
          {!pendingPetParentReport && submittedReport && (
            <ReportSubmittedCard report={submittedReport} />
          )}

          {/* Show pending card first if it exists */}
          {pendingPetParentReport && (
            <ReportCard report={convertPetParentToReport(pendingPetParentReport)} />
          )}

          {/* Show unified missed reports banner with total count */}
          {missedPetParentReports.length > 0 && (
            <Banner
              variant="danger"
              title={
                missedPetParentReports.length === 1
                  ? t(TranslationKeyEnum.MissedReportSingular)
                  : t(TranslationKeyEnum.MissedReportPlural, missedPetParentReports.length)
              }
              subtitle={t(TranslationKeyEnum.SubmitNowToStayInGoodStanding)}
              actionLabel={t(TranslationKeyEnum.ViewAndSubmit)}
              onAction={() => handleBannerAction('pet-parents')}
              className="w-full"
            />
          )}
        </>
      )}
    </ReportSectionLayout>
  );
};

// Adoption Reports Section
export const AdoptionReportsSection = () => {
  const adoptionReports = useAdoptionReportState();
  const adoptionActions = useAdoptionReportActions();

  const { getAdoptionReports } = adoptionActions ?? {};
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (getAdoptionReports && typeof getAdoptionReports === 'function') {
      getAdoptionReports('all', 1, 10);
    }
  }, [getAdoptionReports]);

  // Get highest priority pending adoption report
  const pendingAdoptionReport = adoptionReports?.data
    ? getHighestPriorityPendingReport(adoptionReports.data)
    : undefined;

  // Get missed reports that can still be submitted (actionable missed reports)
  const missedAdoptionReports =
    adoptionReports?.data?.filter(
      (report) => report.status === 'missed' && report.canSubmit === true
    ) ?? [];

  // Get submitted report for the current timeframe
  const submittedReport =
    adoptionReports?.data?.filter(
      (report) => report.status === 'submitted' || report.status === 'late'
    )[0] ?? null;

  const handleBannerAction = (targetTab: 'pet-parents' | 'adoptions') => {
    // Navigate to reports page with the specific report ID to auto-open modal
    navigate(`/reports?tab=${targetTab}`);
  };

  // Convert to Report format for ReportCard and MissedReportBanner
  const convertAdoptionToReport = (adoptionReport: AdoptionReport): Report => {
    let status: ReportStatus;
    if (adoptionReport.status === 'due') {
      status = ReportStatus.Pending;
    } else if (adoptionReport.status === 'missed') {
      status = ReportStatus.Missed;
    } else if (adoptionReport.status === 'late') {
      status = ReportStatus.Late;
    } else {
      status = ReportStatus.Submitted;
    }
    return {
      id: adoptionReport.id,
      type: 'adoption',
      status,
      dueDate: adoptionReport.dueDate ?? '',
      timeframeStart: adoptionReport.timeframe_start,
      isWarning: adoptionReport.isWarning,
    };
  };

  const WelcomeAdoptionReportCard = () => {
    const { t } = useTranslation();
    return (
      <Card
        padding="large"
        radius="large"
        customBackground="bg-gray-50"
        height="h-[280px]"
        shadow={true}
        className="flex justify-between items-end overflow-visible"
      >
        <div className="flex flex-col justify-between h-full">
          <h3 className="text-heading-200 font-bold w-[400px] leading-[1.125em] text-black/90">
            {(() => {
              // Use firstExpectedReport data from backend API if available
              if (adoptionReports?.firstExpectedReport) {
                const submissionStart = utcCalendarDateToLocal(
                  adoptionReports.firstExpectedReport.submissionWindowStart
                );
                const dueMonth = format(submissionStart!, 'MMMM');
                return t(TranslationKeyEnum.NewShelterUserAdoptonReportWelcomeTitle, dueMonth);
              }
              // Fallback: calculate next month as the expected report month
              const nextMonth = addMonths(new Date(), 1);
              const dueMonth = format(nextMonth, 'MMMM');
              return t(TranslationKeyEnum.NewShelterUserAdoptonReportWelcomeTitle, dueMonth);
            })()}
          </h3>
          <div
            className={`inline-flex items-center gap-2 px-3.5 py-1 h-fit border w-max rounded-full bg-blue-tag-bg border-blue-tag-border`}
          >
            <Tooltip
              size={16}
              textClasses={'text-blue-tag-text'}
              content={
                <div className="w-[360px]  p-4 flex flex-col gap-4">
                  <div>
                    {t(TranslationKeyEnum.AdoptionTooltipPrefix)}{' '}
                    {t(TranslationKeyEnum.AdoptionTooltipLinkText)}
                    {/* temporarily commenting until we have the document link */}
                    {/* <a href="#" className="underline">
                                        {t(TranslationKeyEnum.AdoptionTooltipLinkText)}
                                      </a> */}
                  </div>
                  <div>{t(TranslationKeyEnum.AdoptionTooltipSuffix)}</div>
                </div>
              }
            />{' '}
            <span className={`text-sm font-bold text-blue-tag-text`}>
              {t(TranslationKeyEnum.LearnMore)}
            </span>
          </div>
        </div>
      </Card>
    );
  };

  const AdoptionReportSubmittedCard = ({ report }: { report: AdoptionReport }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    return (
      <Card
        padding="large"
        radius="large"
        customBackground="bg-gray-50"
        height="h-[280px]"
        shadow={true}
        className="flex justify-between items-end"
      >
        <div className="flex flex-col justify-between h-full">
          <h3 className="text-heading-200 font-bold w-[320px] leading-[1.125em] text-black/90">
            {t(
              TranslationKeyEnum.AdoptionReportSubmittedTitle,
              // FIX: Convert UTC date string to local Date preserving calendar date
              format(utcCalendarDateToLocal(report.timeframe_start)!, 'MMMM')
            )}
          </h3>
          <span className={`text-sm font-light`}>
            {t(
              TranslationKeyEnum.AdoptionReportSubmittedNextWindow,
              // FIX: Convert UTC date string to local Date before using with addMonths
              format(addMonths(utcCalendarDateToLocal(report.timeframe_start)!, 1), 'MMM.'),
              format(addMonths(utcCalendarDateToLocal(report.timeframe_start)!, 2), 'MMM.')
            )}
          </span>
        </div>
        <Button
          label={t(TranslationKeyEnum.ViewPastReports)}
          type={ButtonType.Primary}
          size={ButtonSize.Small}
          onClick={() => {
            // Navigate to the reports page with the appropriate tab and auto-open the specific report
            navigate(`/reports?tab=adoptions`);
          }}
          className="!w-[32] h-[40px]"
        />
      </Card>
    );
  };

  const MissedAdoptionReportCard = ({ report }: { report: AdoptionReport }) => {
    const { t } = useTranslation();
    return (
      <Card
        padding="large"
        radius="large"
        customBackground="bg-gray-50"
        height="h-[280px]"
        shadow={true}
        className="flex justify-between items-end"
      >
        <div className="flex flex-col justify-between h-full">
          <h3 className="text-heading-200 font-bold w-[400px] leading-[1.125em] text-black/90">
            {t(
              TranslationKeyEnum.AdoptionReportMissedSubmissionTitle,
              // FIX: Convert UTC date string to local Date preserving calendar date
              format(utcCalendarDateToLocal(report.timeframe_start)!, 'MMMM')
            )}
          </h3>
        </div>
      </Card>
    );
  };

  return (
    <ReportSectionLayout
      title={t(TranslationKeyEnum.AdoptionReports)}
      description={t(TranslationKeyEnum.SubmitAdoptionTotals)}
      viewPastReportsUrl="/reports?tab=adoptions"
    >
      {adoptionReports === null ? (
        <div>{t(TranslationKeyEnum.LoadingAdoptionReports)}</div>
      ) : (
        <>
          {/* Render cards based on the priority order, ensuring only one card is visible at a time */}
          {(() => {
            if (adoptionReports.data.length === 0) {
              // 1. Show the Welcome Card if there are no adoption reports
              return <WelcomeAdoptionReportCard />;
            } else if (
              !pendingAdoptionReport &&
              missedAdoptionReports.length === 0 &&
              submittedReport
            ) {
              // 2. Show the Report Submitted Card if there is no pending report, no missed report and a report has been submitted
              return <AdoptionReportSubmittedCard report={submittedReport} />;
            } else if (missedAdoptionReports.length > 0) {
              // 3. Show the Missed Adoption Report Card if there are missed adoption reports
              return <MissedAdoptionReportCard report={missedAdoptionReports[0]} />;
            } else if (pendingAdoptionReport) {
              // 4. Show the Report Card for pending reports if there is a pending adoption report
              return <ReportCard report={convertAdoptionToReport(pendingAdoptionReport)} />;
            }
            return null;
          })()}

          {/* Show unified missed reports banner with total count */}
          {missedAdoptionReports.length > 0 && (
            <Banner
              variant="danger"
              title={
                missedAdoptionReports.length === 1
                  ? t(TranslationKeyEnum.MissedReportSingular)
                  : t(TranslationKeyEnum.MissedReportPlural, missedAdoptionReports.length)
              }
              subtitle={t(TranslationKeyEnum.SubmitNowToStayInGoodStanding)}
              actionLabel={t(TranslationKeyEnum.ViewAndSubmit)}
              onAction={() => handleBannerAction('adoptions')}
              className="w-full"
            />
          )}
        </>
      )}
    </ReportSectionLayout>
  );
};
